import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Cpu, MapPin } from 'lucide-react';
interface SlideCampaignEngineProps {
  onContinue: () => void;
}
export function SlideCampaignEngine({
  onContinue
}: SlideCampaignEngineProps) {
  const [stage, setStage] = useState(0);
  const master = ['Strategy', 'Hook', 'Email & banner assets', 'Teaser Snaks', 'Trailer Snaks', 'Calls to action', 'Measurement framework', 'Optimization recommendations'];
  const markets = ['DE', 'FR', 'ES', 'IT', 'NL', 'DK', 'UK', 'PL'];
  useEffect(() => {
    const timers = [300, 900, 1600, 2400, 3100].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-222" data-mp="2zn9y334ymvcn">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3" data-id="element-223" data-mp="33ov5wa2n0jfz">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-224" data-mp="1u2d7kxzro9q3">
          The Solution
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-225" data-mp="1k69wv3qun2rj">
          One campaign engine, many local executions
        </h2>
        <p className="text-lg text-white/60" data-id="element-226" data-mp="1g46hi4127e0z">
          Snakker becomes the central campaign office.
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center max-w-6xl mx-auto w-full space-y-6" data-id="element-227" data-mp="1b7y9rbp6yn3u">
        <div className="grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-6" data-id="element-228" data-mp="16d22x5fapv6o">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          y: stage >= 1 ? 0 : 16
        }} className="bg-teal-400/10 border border-teal-400/30 rounded-2xl p-6" data-id="element-229" data-mp="11gtra20b4k49">
            <div className="flex items-center gap-3 mb-5" data-id="element-230" data-mp="nidogm5kfdre">
              <div className="w-10 h-10 rounded-xl bg-teal-400/20 text-teal-400 flex items-center justify-center" data-id="element-231" data-mp="drlvjywryskl">
                <Cpu className="w-5 h-5" data-id="element-232" data-mp="hv1syxgo4wyc" />
              </div>
              <div data-id="element-233" data-mp="3v0prbcxldfp">
                <h3 className="text-xl font-semibold text-white" data-id="element-234" data-mp="3vivqzpeb60t">
                  The master campaign
                </h3>
                <p className="text-white/50 text-sm" data-id="element-235" data-mp="8u3sgp5fv7ki">Built once, centrally</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-id="element-236" data-mp="wl99h7n485we">
              {master.map((item, idx) => <motion.div key={item} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: stage >= 2 ? 1 : 0,
              y: stage >= 2 ? 0 : 8
            }} transition={{
              delay: stage >= 2 ? idx * 0.07 : 0
            }} className="flex items-center gap-2.5 text-white/85 text-sm bg-navy-900/50 border border-navy-700 rounded-xl px-3 py-2.5" data-id="element-237" data-mp="mtd030fkwtcv" data-mpi={idx}>
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" data-id="element-238" data-mp="2zzt5xlbouih1" />
                  {item}
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          y: stage >= 3 ? 0 : 16
        }} className="bg-navy-700/40 border border-navy-600 rounded-2xl p-6 flex flex-col" data-id="element-239" data-mp="39qo33kqh32h9">
            <div className="flex items-center gap-3 mb-5" data-id="element-240" data-mp="2q1vfqsa9qbls">
              <div className="w-10 h-10 rounded-xl bg-sky-400/15 text-sky-400 flex items-center justify-center" data-id="element-241" data-mp="2q0c0n50cm6ll">
                <MapPin className="w-5 h-5" data-id="element-242" data-mp="a8wkvdohq5js" />
              </div>
              <div data-id="element-243" data-mp="5amncwfamsv0">
                <h3 className="text-xl font-semibold text-white" data-id="element-244" data-mp="5b4oo7gtbx9s">
                  Local activation
                </h3>
                <p className="text-white/50 text-sm" data-id="element-245" data-mp="10nxfwgl2mgwj">
                  Adapted per market need
                </p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2.5" data-id="element-246" data-mp="1aiz6lguzvr18">
              {markets.map((m, idx) => <motion.div key={m} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: stage >= 4 ? 1 : 0,
              scale: stage >= 4 ? 1 : 0.9
            }} transition={{
              delay: stage >= 4 ? idx * 0.06 : 0
            }} className="bg-navy-900/50 border border-navy-700 rounded-xl py-3 text-center text-white font-semibold text-sm" data-id="element-247" data-mp="1fk50vj4azmcv" data-mpi={idx}>
                  {m}
                </motion.div>)}
            </div>
            <p className="text-white/60 text-sm leading-relaxed mt-5 pt-4 border-t border-navy-600" data-id="element-248" data-mp="277x6fdhlqf59">
              Local teams adapt language, emphasis, channels and calls to action
              — without rebuilding the campaign.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 5 ? 1 : 0,
        y: stage >= 5 ? 0 : 12
      }} className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-249" data-mp="3itq8lm81xd3a">
          {['Global consistency', 'Local relevance', 'Faster execution'].map(item => <div key={item} className="bg-navy-900/50 border border-teal-400/30 rounded-2xl px-5 py-4 text-center" data-id="element-250" data-mp="m0tlv7q29hh">
              <p className="text-white font-semibold text-lg" data-id="element-251" data-mp="y2zjh6y5p8tx">{item}</p>
            </div>)}
        </motion.div>
      </div>
    </div>;
}